import { logger } from "@/lib/logger";

/**
 * Named text highlights painted with the CSS Custom Highlight API (ADR 0001),
 * styled with `::highlight(name)`. Highlighting never touches the document DOM,
 * so React's view of it stays true.
 *
 * `CSS.highlights` is global per page, so each owner (a hook, a renderer) keeps
 * its own ranges here and the union per name is what gets published. An owner
 * sets and clears only its own ranges.
 */

/** Higher paints on top: search-current, search, reference-preview, reference. */
const HIGHLIGHT_PRIORITY = {
  reference: 0,
  "reference-preview": 1,
  search: 2,
  "search-current": 3,
} as const;

export type HighlightName = keyof typeof HIGHLIGHT_PRIORITY;

export interface HighlightOwner {
  /** Replaces this owner's ranges under `name`. */
  set(name: HighlightName, ranges: Iterable<AbstractRange>): void;
  /** Removes this owner's ranges under `name`. */
  clear(name: HighlightName): void;
  /** Removes every range this owner published. */
  clearAll(): void;
}

const rangesByName = new Map<
  HighlightName,
  Map<HighlightOwner, AbstractRange[]>
>();

/**
 * Chrome and Edge 105, Safari 17.2, Firefox 140. Elsewhere ranges are still
 * accepted and nothing is painted, so callers never branch.
 */
const isHighlightApiAvailable = (): boolean =>
  typeof Highlight === "function" &&
  typeof CSS !== "undefined" &&
  "highlights" in CSS;

let warnedApiUnavailable = false;

function publish(name: HighlightName): void {
  if (!isHighlightApiAvailable()) {
    if (!warnedApiUnavailable) {
      warnedApiUnavailable = true;
      logger.warn(
        "Custom Highlight API unavailable, highlights are not painted",
        {
          operation: "publishHighlight",
          highlightName: name,
        }
      );
    }
    return;
  }
  const owners = rangesByName.get(name);
  if (!owners?.size) {
    CSS.highlights.delete(name);
    return;
  }
  // `new Highlight(...ranges)` overflows the call stack at ~100k ranges.
  const highlight = new Highlight();
  for (const ranges of owners.values()) {
    for (const range of ranges) highlight.add(range);
  }
  highlight.priority = HIGHLIGHT_PRIORITY[name];
  CSS.highlights.set(name, highlight);
}

export function createHighlightOwner(): HighlightOwner {
  const owner: HighlightOwner = {
    set(name, ranges) {
      const owners = rangesByName.get(name) ?? new Map();
      rangesByName.set(name, owners);
      const ownRanges = Array.from(ranges);
      if (ownRanges.length > 0) {
        owners.set(owner, ownRanges);
      } else {
        owners.delete(owner);
      }
      publish(name);
    },
    clear(name) {
      if (rangesByName.get(name)?.delete(owner)) publish(name);
    },
    clearAll() {
      for (const name of rangesByName.keys()) owner.clear(name);
    },
  };
  return owner;
}
